<template>
  <div>
    <!-- Elfsight Widget Div'leri -->
    <div
      v-for="id in elfsightAppIDs"
      :key="id"
      :class="`elfsight-app-${id}`"
      data-elfsight-app-lazy
    ></div>

    <!-- FAB Buton -->
    <div class="fab-menu-container">
      <button @click="toggleWidgets" class="fab-btn" :class="{ active: isWidgetsVisible }">
        <img :src="whiteLogo" alt="Divissa Logo" class="fab-logo" width="26" height="26" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import whiteLogo from '~/assets/images/logo-icon.svg';

const isWidgetsVisible = ref(false);
const areWidgetsInitialized = ref(false);

// Elfsight Widget ID'leri
const elfsightAppIDs = [
  '85cd4d9d-c978-4e56-852b-4a1d58f1fa61', // WhatsApp
  'e54a5edb-0cd7-45dd-a0b3-99a7d4bc24f2', // Randevu
];

// Widget script tanımlamaları
const widgetScripts = [
  {
    id: 'elfsight-platform',
    src: 'https://static.elfsight.com/platform/platform.js',
  },
  // {
  //   id: 'freshchat',
  //   src: 'https://fw-cdn.com/12233859/4709192.js',
  // },
];

function addInitialCSS() {
  const styleEl = document.createElement('style');
  styleEl.id = 'elfsight-initial-hiding';
  styleEl.innerHTML = `
    /* Elfsight widget'larını başlangıçta gizle */
.es-portal-root,
.portal-nest-provider__ElfsightPortalNest-sc-911e6349-0,
div[class^="eapps-widget-container"] {
  display: none !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: none !important;
}
  `;
  document.head.appendChild(styleEl);
}

async function loadScripts(): Promise<void> {
  
  for (const script of widgetScripts) {
    if (!document.getElementById(script.id)) {
      console.log(`${script.id} yükleniyor...`);
      
      await new Promise<void>((resolve) => {
        const scriptEl = document.createElement('script');
        scriptEl.id = script.id;
        scriptEl.src = script.src;
        scriptEl.async = true;
        scriptEl.onload = () => {
          resolve();
        };
        scriptEl.onerror = () => {
          resolve();
        };
        document.head.appendChild(scriptEl);
      });
    } 
  }
  
}

function toggleWidgets(): void {
  isWidgetsVisible.value = !isWidgetsVisible.value;
  setWidgetsVisibility(isWidgetsVisible.value);
}

function setWidgetsVisibility(visible: boolean): void {
  try {
    let widgetCount = 0;

    const elfsightAllElements = document.querySelectorAll(
      '.es-portal-root, .portal-nest-provider__ElfsightPortalNest-sc-911e6349-0, [class*="eapps-appointment-booking"], [class*="eapps-whatsapp-chat"]'
    );

    const showStyle = `
      display: block !important;
      opacity: 1 !important;
      pointer-events: auto !important;
    `;
    const hideStyle = `
      display: none !important;
      opacity: 0 !important;
      pointer-events: none !important;
    `;

    elfsightAllElements.forEach(element => {
      (element as HTMLElement).style.cssText = visible ? showStyle : hideStyle;
      widgetCount++;
    });

    // Randevu widget'ı için ek güvenlik
    if (visible) {
      setTimeout(() => {
        const appointmentElements = document.querySelectorAll('[class*="eapps-appointment-booking"]');
        appointmentElements.forEach(element => {
          (element as HTMLElement).style.cssText = showStyle;
        });
      }, 100);
    }

    console.log(`Elfsight widget görünürlüğü: ${visible ? 'gösterildi' : 'gizlendi'} (${widgetCount} öğe)`);
  } catch (error) {
    console.error('Widget görünürlük hatası:', error);
  }
}


async function initializeWidgets(): Promise<void> {
  
  addInitialCSS();
  
  try {
    await loadScripts();
    
    await waitForElfsightWidgets();
    
    areWidgetsInitialized.value = true;
  } catch (error) {
    console.error('Widget başlatma hatası:', error);
  }
}

async function waitForElfsightWidgets(): Promise<void> {
  
  return new Promise<void>((resolve) => {
    let checkCount = 0;
    let widgetsFound = false;
    
    const checkInterval = setInterval(() => {
      const elfsightElements = document.querySelectorAll(
        '.es-portal-root, .portal-nest-provider__ElfsightPortalNest-sc-911e6349-0, [class*="eapps-appointment-booking"], [class*="eapps-whatsapp-chat"]'
      );
      
      if (elfsightElements.length > 0) {
        setWidgetsVisibility(false);
        
        widgetsFound = true;
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        resolve();
      } 
      
      if (checkCount >= 40) { 
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        resolve();
      }
      
      checkCount++;
    }, 500);
    
    const timeoutId = setTimeout(() => {
      if (!widgetsFound) {
        clearInterval(checkInterval);
        resolve();
      }
    }, 20000);
  });
}


onMounted(() => {
  initializeWidgets();
});

onBeforeUnmount(() => {
  
  const styleEl = document.getElementById('elfsight-initial-hiding');
  if (styleEl) {
    styleEl.remove();
  }
});
</script>

<style lang="less">
.fab-menu-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;

  .fab-btn {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(to right, var(--color-title), var(--color-background-blue));
    border: none;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    position: relative;
    z-index: 10000;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &.active {
      transform: rotate(45deg);
    }

    &:hover { scale: 1.1 }
    &.rotate-45 { transform: rotate(45deg) }
    img.rotate-180 { transform: rotate(180deg) }

    .fab-logo {
      transition: transform 0.5s ease;
    }
  }
}


/* Diğer stil tanımları */
.trustpilot-button-wrapper {
  position: fixed;
  bottom: 150px;
  right: 10px;
  z-index: 99999;
  margin: auto 10px auto auto;
  color: white;
  text-decoration: none;

  .trustpilot-button {
    display: inline-flex;
    align-items: center;
    padding: 10px;
    background-color: #000032;
    color: white;
    font-weight: bold;
    border-radius: 5px;
    border: none;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: #000032;
    }

    .trust-pilot-icon {
      margin-right: 10px;
    }
  }
}

.ButtonBase__Overlay-sc-c65ab2ce-4 {
  padding: 18px !important;
}

.action-button__CenteredButton-sc-ddd1bfed-0 {
  height: auto !important;
}

.hCawpU {
  bottom: 160px !important;
  margin: 20px 20px 10px 20px !important;
}

.ieKKqV {
  left: auto !important;
  right: 0 !important;
  bottom: 75px !important;
  top: auto !important;
}

.EfQos {
  height: 100% !important;
}

.dLhUFN {
  width: 100% !important;
  height: 100% !important;
}

.juGgwj {
  z-index: 100;
}

.kCELwi {
  height: auto !important;
}
</style>